import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { Trophy, Lock, Eye, Globe, EyeOff } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { Empty, ConfirmAction } from "@/components/Shared";
import { useRefreshOnPull } from "@/components/PullToRefresh";
import { ClassificaVisibilita } from "@/pages/Impostazioni";

export default function Classifica() {
  const [data, setData] = useState(null);
  const [stato, setStato] = useState(null);
  const [negato, setNegato] = useState(false);
  const [pubblicando, setPubblicando] = useState(false);
  const [vista, setVista] = useState("generale"); // "generale" | id categoria

  const load = useCallback(() => {
    api.get("/classifica/stato").then((r) => setStato(r.data)).catch(() => {});
    api.get("/classifica")
      .then((r) => { setData(r.data); setNegato(false); })
      .catch((e) => { if (e?.response?.status === 403) { setNegato(true); setData(null); } });
  }, []);
  useEffect(() => { load(); }, [load]);
  useRefreshOnPull(load);

  const setPubblica = async (pubblica) => {
    setPubblicando(true);
    try {
      await api.put("/classifica/pubblica", { pubblica });
      toast.success(pubblica ? "Classifica resa pubblica: ora la vedono tutti" : "Classifica nascosta");
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setPubblicando(false); }
  };

  // Accesso negato (es. responsabile non designato che arriva via URL)
  if (negato) {
    return (
      <div data-testid="classifica-page">
        <h1 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mb-1">Classifica generale</h1>
        <div className="card-app p-8 text-center mt-6" data-testid="classifica-non-visibile">
          <Lock className="mx-auto h-10 w-10 text-slate-300 mb-3" strokeWidth={1.5} />
          <p className="font-display font-bold text-slate-600">Classifica non ancora pubblica</p>
          <p className="text-sm text-slate-400 font-semibold mt-1">
            Sarà visibile quando il responsabile incaricato deciderà di pubblicarla.
          </p>
        </div>
      </div>
    );
  }

  if (!data) return <p className="text-slate-400 font-semibold">Caricamento...</p>;

  const { scala_default } = data;
  const categorie = data.categorie || [];
  const pubblica = stato?.pubblica;

  // Classifica attiva: generale o quella della categoria selezionata
  const catAttiva = vista === "generale" ? null : categorie.find((c) => c.id === vista);
  const classifica = catAttiva ? catAttiva.classifica : data.classifica;
  const podio = classifica.slice(0, 3);

  return (
    <div data-testid="classifica-page">
      <h1 className="font-display text-2xl md:text-3xl font-extrabold text-slate-900 mb-1">
        {catAttiva ? `Classifica · ${catAttiva.nome}` : "Classifica generale"}
      </h1>
      <p className="text-sm text-slate-500 font-semibold mb-4">Scala punti: {scala_default.join(" · ")}</p>

      {/* Banner di stato + azione pubblica (solo admin / designato) */}
      {stato && (stato.puoi_pubblicare || !pubblica) && (
        <div className={`card-app p-4 mb-5 flex items-center gap-3 flex-wrap ${pubblica ? "border border-emerald-200 bg-emerald-50/40" : "border border-amber-200 bg-amber-50/40"}`} data-testid="classifica-stato">
          {pubblica ? <Globe className="h-5 w-5 text-emerald-500 shrink-0" /> : <Eye className="h-5 w-5 text-amber-500 shrink-0" />}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-bold text-slate-800">
              {pubblica ? "Classifica pubblica" : "Anteprima riservata"}
            </p>
            <p className="text-xs text-slate-500 font-semibold">
              {pubblica
                ? "Tutti gli animatori possono vederla."
                : "La vedi in anteprima. Gli animatori non la vedono finché non la pubblichi."}
            </p>
          </div>
          {stato.puoi_pubblicare && (
            pubblica ? (
              <ConfirmAction
                title="Nascondere di nuovo la classifica?"
                description="Gli animatori non potranno più vederla, finché non la ripubblichi."
                onConfirm={() => setPubblica(false)}
                testId="classifica-nascondi-confirm">
                <button type="button" className="btn-ghost !py-2 text-xs shrink-0" disabled={pubblicando} data-testid="classifica-nascondi-btn">
                  <EyeOff className="h-3.5 w-3.5" /> Nascondi
                </button>
              </ConfirmAction>
            ) : (
              <ConfirmAction
                title="Rendere pubblica la classifica?"
                description="Da questo momento tutti gli animatori e gli aiuto animatori potranno vedere la classifica generale."
                onConfirm={() => setPubblica(true)}
                testId="classifica-pubblica-confirm">
                <button type="button" className="btn-primary !py-2 text-xs shrink-0" disabled={pubblicando} data-testid="classifica-pubblica-btn">
                  <Globe className="h-3.5 w-3.5" /> Rendi pubblica
                </button>
              </ConfirmAction>
            )
          )}
        </div>
      )}

      {stato?.puoi_designare && <ClassificaVisibilita />}

      {/* Pills: Generale prima, poi una per categoria */}
      {categorie.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5" data-testid="classifica-pills">
          <button type="button" onClick={() => setVista("generale")}
            className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${vista === "generale" ? "bg-slate-900 text-white shadow" : "bg-white border border-slate-200 text-slate-600 hover:border-amber-400"}`}
            data-testid="pill-generale">
            <Trophy className="h-3.5 w-3.5" /> Generale
          </button>
          {categorie.map((c) => {
            const active = vista === c.id;
            return (
              <button key={c.id} type="button" onClick={() => setVista(c.id)}
                className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold transition-colors ${active ? "text-white shadow" : "bg-white border border-slate-200 text-slate-600 hover:border-amber-400"}`}
                style={active ? { backgroundColor: c.colore } : undefined}
                data-testid={`pill-${c.id}`}>
                <span className="h-2 w-2 rounded-full shrink-0" style={{ backgroundColor: active ? "#fff" : c.colore }} />
                {c.nome}
              </button>
            );
          })}
        </div>
      )}

      {classifica.length === 0 ? (
        <Empty icon={Trophy} text="Nessuna squadra in classifica" sub="La classifica apparirà quando verranno create le squadre" />
      ) : catAttiva && classifica.every((s) => s.giochi_completati === 0) ? (
        <div className="card-app p-8 text-center" data-testid="categoria-vuota">
          <Trophy className="mx-auto h-10 w-10 text-slate-300 mb-3" strokeWidth={1.5} />
          <p className="font-display font-bold text-slate-500">Ancora nessun gioco completato</p>
          <p className="text-sm text-slate-400 font-semibold mt-1">In questa categoria non ci sono ancora partite con tutte le squadre.</p>
        </div>
      ) : (
        <>
          {podio.length >= 2 && (
            <div className="flex items-end justify-center gap-3 mb-8 max-w-lg mx-auto">
              {[podio[1], podio[0], podio[2]].filter(Boolean).map((s, i) => {
                const h = i === 1 ? "h-32" : i === 0 ? "h-24" : "h-20";
                const medal = i === 1 ? "🥇" : i === 0 ? "🥈" : "🥉";
                return (
                  <motion.div key={s.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.15 }} className="flex-1 flex flex-col items-center" data-testid={`podio-${s.posizione}`}>
                    <span className="text-3xl mb-1">{medal}</span>
                    <p className="font-display font-extrabold text-slate-800 text-sm text-center truncate w-full">{s.nome}</p>
                    <p className="font-display font-extrabold text-amber-600 text-lg mb-2">{s.punti} pt</p>
                    <div className={`w-full ${h} rounded-t-3xl`} style={{ background: s.colore || "#cbd5e1" }} />
                  </motion.div>
                );
              })}
            </div>
          )}

          <div className="card-app divide-y divide-slate-50" data-testid="classifica-lista">
            {classifica.map((s, i) => (
              <motion.div key={s.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: Math.min(i * 0.05, 0.5) }}
                className="flex items-center gap-4 px-5 py-4" data-testid={`classifica-riga-${s.id}`}>
                <span className={`font-display font-extrabold text-xl w-9 text-center ${s.posizione === 1 ? "text-amber-500" : s.posizione === 2 ? "text-slate-400" : s.posizione === 3 ? "text-orange-400" : "text-slate-300"}`}>
                  {s.posizione}°
                </span>
                <span className="h-4 w-4 rounded-full shrink-0 border-2 border-white shadow" style={{ background: s.colore || "#94a3b8" }} />
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-slate-800 truncate">{s.nome}</p>
                  <p className="text-xs text-slate-400 font-semibold">{s.giochi_completati} giochi completati</p>
                </div>
                <p className="font-display font-extrabold text-slate-900 text-lg">{s.punti} <span className="text-xs text-slate-400">pt</span></p>
              </motion.div>
            ))}
          </div>
          <p className="text-xs text-slate-400 font-semibold mt-4 text-center">
            In caso di parità di punteggio grezzo le squadre ricevono i punti della stessa posizione; la posizione successiva slitta.
          </p>
        </>
      )}
    </div>
  );
}
